import {
  RecordMap,
  RecordTable,
  RecordValue,
  setMapRecord,
  tableProps,
} from "./schema";

// Code adopted from the 'sql-template-tag' package
// https://github.com/blakeembrey/sql-template-tag
// MIT license

/**
 * Values supported by SQL engine.
 */
export type Value = unknown;
/**
 * Supported value or SQL instance.
 */
export type RawValue = Value | Sql;

/**
 * A SQL instance can be nested within each other to build SQL strings.
 */
export class Sql {
  readonly values: Value[];
  readonly strings: string[];
  constructor(rawStrings: readonly string[], rawValues: readonly RawValue[]) {
    if (rawStrings.length - 1 !== rawValues.length) {
      if (rawStrings.length === 0) {
        throw new TypeError("Expected at least 1 string");
      }
      throw new TypeError(
        `Expected ${rawStrings.length} strings to have ${
          rawStrings.length - 1
        } values`,
      );
    }
    const valuesLength = rawValues.reduce(
      (len: number, value) =>
        len + (value instanceof Sql ? value.values.length : 1),
      0,
    ) as number;

    this.values = new Array(valuesLength);
    this.strings = new Array(valuesLength + 1);
    this.strings[0] = rawStrings[0]!;
    // Iterate over raw values, strings, and children. The value is always
    // positioned between two strings, e.g. `index + 1`.
    let i = 0,
      pos = 0;

    while (i < rawValues.length) {
      const child = rawValues[i++];
      const rawString = rawStrings[i];
      // Check for nested `sql` queries.
      if (child instanceof Sql) {
        // Append child prefix text to current string.
        this.strings[pos] += child.strings[0];
        let childIndex = 0;
        while (childIndex < child.values.length) {
          this.values[pos++] = child.values[childIndex++];
          this.strings[pos] = child.strings[childIndex]!;
        }
        // Append raw string to current string.
        this.strings[pos] += rawString;
      } else {
        this.values[pos++] = child;
        this.strings[pos] = rawString!;
      }
    }
  }

  get text() {
    let i = 1,
      value = this.strings[0];
    while (i < this.strings.length) value += `$${i}${this.strings[i++]}`;
    return value as string;
  }

  get sql() {
    let i = 1,
      value = this.strings[0];
    while (i < this.strings.length) value += `?${this.strings[i++]}`;
    return value as string;
  }

  inspect() {
    return {
      text: this.text,
      sql: this.sql,
      values: this.values,
    };
  }
}
/**
 * Create a SQL query for a list of values.
 */
export function join(
  values: readonly RawValue[],
  separator = ",",
  prefix = "",
  suffix = "",
): Sql {
  if (values.length === 0) {
    throw new TypeError(
      "Expected `join([])` to be called with an array of multiple elements, but got an empty array",
    );
  }
  return new Sql(
    [prefix, ...Array(values.length - 1).fill(separator), suffix],
    values,
  );
}
/**
 * Create a SQL query for a list of structured values.
 */
export function bulk(
  data: ReadonlyArray<ReadonlyArray<RawValue>>,
  separator = ",",
  prefix = "",
  suffix = "",
): Sql {
  const length = data.length && data[0]!.length;
  if (length === 0) {
    throw new TypeError(
      "Expected `bulk([][])` to be called with a nested array of multiple elements, but got an empty array",
    );
  }
  const values = data.map((item, index) => {
    if (item.length !== length) {
      throw new TypeError(
        `Expected \`bulk([${index}][])\` to have a length of ${length}, but got ${item.length}`,
      );
    }
    return new Sql(["(", ...Array(item.length - 1).fill(separator), ")"], item);
  });
  return new Sql(
    [prefix, ...Array(values.length - 1).fill(separator), suffix],
    values,
  );
}
/**
 * Create raw SQL statement.
 */
export function raw(value: string): Sql {
  return new Sql([value], []);
}
/**
 * Placeholder value for "no text".
 */
export const empty: Sql = raw("");
/**
 * Create a SQL object from a template string.
 */
export default function _sql(
  strings: readonly string[],
  ...values: readonly RawValue[]
): Sql {
  return new Sql(strings, values);
}

export type SqlValue =
  | string
  | number
  | null
  | bigint
  | Uint8Array
  | Int8Array
  | ArrayBuffer;

export type Statement = {
  // /** The sqlite formatted version of the query */
  // sql: string;
  /** The postgres formatted version of the query */
  text: string;
  // params?: SqlValue[] | Record<string, SqlValue>;
  /**
   * This is an alternative, less type safe option for playing nicely
   * with the sql-template-tag package
   */
  values?: any[];
};

const sql = _sql as typeof _sql & {
  EMPTY: typeof empty;
  join: typeof join;
  raw: typeof raw;
};

sql.EMPTY = empty;
sql.join = join;
sql.raw = raw;

export function namespaceAllTableColumns<T extends RecordTable>(
  ...tables: T[]
) {
  return sql.join(
    tables.flatMap((table) =>
      tableProps[table].map((prop) =>
        sql.raw(`"${table}".${prop} as "${table}__${prop}"`),
      ),
    ),
    ",\n",
  );
}

export function extractRecordsFromNamespacedRows(args: {
  rows: Record<string, any>[];
  tables: RecordTable[];
  decodeRecord?: (
    table: RecordTable,
    record: Record<string, any>,
  ) => RecordValue;
}): RecordMap {
  const { rows, tables, decodeRecord } = args;
  const recordMap: RecordMap = {};

  for (const row of rows) {
    for (const table of tables) {
      const props = tableProps[table];
      const record = {} as any;

      for (const prop of props) {
        record[prop] = row[`${table}__${prop}`];
      }

      setMapRecord(
        recordMap,
        { table, id: record.id },
        decodeRecord ? decodeRecord(table, record) : record,
      );
    }
  }

  return recordMap;
}

export { sql };
